import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { useClient } from '../../../contexts/ClientContext';
import { getManualCredits, getManualCreditsSummary } from '../../../services/cloudopsApi';

interface CreditCode {
  code: string;
  amount: number;
  aws_account_id?: string;
  expiration_date?: string;
}

export interface PartnerCentralCreditsData {
  active_credit_codes_count: number;
  total_credits_available: number;
  total_credits_allocated?: number;
  credit_codes?: CreditCode[];
}

interface BurnRateTrendItem {
  credits_remaining?: number | null;
  gross_cost?: number;
  actual?: number;
}

interface ManualCredit {
  id: string;
  credit_name: string;
  remaining_amount: number;
  aws_account_id?: string;
  end_date?: string;
}

interface ManualCreditsSummary {
  total_credits: number;
}

interface PartnerCentralCreditsProps {
  data: PartnerCentralCreditsData | null;
  burnRateTrend: BurnRateTrendItem[] | null;
  loading: boolean;
}

const PartnerCentralCredits: React.FC<PartnerCentralCreditsProps> = ({ data, burnRateTrend, loading }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedClientId } = useClient();
  const [manualCredits, setManualCredits] = useState<ManualCredit[]>([]);
  const [manualCreditsSummary, setManualCreditsSummary] = useState<ManualCreditsSummary | null>(null);
  const [, setLoadingManualCredits] = useState<boolean>(false);

  // Fetch manual credits when component mounts or client changes
  useEffect(() => {
    const fetchManualCredits = async () => {
      const clientId = selectedClientId || user?.client_id;
      if (!clientId) return;

      setLoadingManualCredits(true);
      try {
        const [creditsData, summaryData] = await Promise.all([
          getManualCredits({ client_id: clientId }),
          getManualCreditsSummary(clientId)
        ]);
        setManualCredits(creditsData || []);
        setManualCreditsSummary(summaryData);
      } catch (error: unknown) {
        console.error('Error fetching manual credits:', error);
        setManualCredits([]);
        setManualCreditsSummary(null);
      } finally {
        setLoadingManualCredits(false);
      }
    };

    fetchManualCredits();
  }, [selectedClientId, user?.client_id]);

  // Extract calculated credits remaining from burn rate trend
  const calculatedCreditsRemaining: number | null = (burnRateTrend?.length ?? 0) > 0
    ? burnRateTrend![burnRateTrend!.length - 1].credits_remaining ?? null
    : null;

  // Calculate depletion date based on recent spend trend
  const calculateDepletionDate = (): { date: Date; daysRemaining: number } | null => {
    if (!burnRateTrend || burnRateTrend.length < 7 || !calculatedCreditsRemaining || calculatedCreditsRemaining <= 0) {
      return null;
    }

    // Get last 7 days of data to calculate average daily AWS consumption
    const recentData = burnRateTrend.slice(-7);
    const dailyConsumption = [];

    for (let i = 1; i < recentData.length; i++) {
      // Use gross_cost (actual AWS consumption) not actual (out-of-pocket)
      // This gives us the true consumption rate regardless of credits
      const dailyGross = (recentData[i].gross_cost || 0) - (recentData[i - 1].gross_cost || 0);
      if (dailyGross > 0) {
        dailyConsumption.push(dailyGross);
      }
    }

    if (dailyConsumption.length === 0) {
      return null;
    }

    // Calculate average daily consumption
    const avgDailyConsumption = dailyConsumption.reduce((sum, val) => sum + val, 0) / dailyConsumption.length;

    if (avgDailyConsumption <= 0) {
      return null;
    }

    // Calculate days until credits are exhausted
    const daysUntilDepletion = Math.floor(calculatedCreditsRemaining / avgDailyConsumption);

    // Calculate depletion date
    const depletionDate = new Date();
    depletionDate.setDate(depletionDate.getDate() + daysUntilDepletion);

    return { date: depletionDate, daysRemaining: daysUntilDepletion };
  };

  const depletionInfo = calculateDepletionDate();

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
          Credits
        </h3>
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      </div>
    );
  }

  if (!data || (data.active_credit_codes_count === 0 && manualCredits.length === 0)) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
            Credits
          </h3>
          <button
            onClick={() => navigate('/funding')}
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            View Funding →
          </button>
        </div>
        <div className="flex flex-col items-center justify-center h-32 text-gray-500 dark:text-gray-400">
          <svg className="h-12 w-12 mb-3 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-center text-sm">
            No credits available
            <br />
            <span className="text-xs">Add credits from Partner Central or manually enter credits</span>
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate total allocated including manual credits
  const totalAllocated = (data?.total_credits_allocated || 0) + (manualCreditsSummary?.total_credits || 0);

  const creditCodesSuffix = data.active_credit_codes_count === 1 ? '' : 's';
  const creditCodesLabel = calculatedCreditsRemaining == null
    ? `${data.active_credit_codes_count} active credit code${creditCodesSuffix}`
    : 'Calculated remaining';

  return (
    <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white">
          Credits
        </h3>
        <button
          onClick={() => navigate('/funding')}
          className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
        >
          View All →
        </button>
      </div>

      {/* Credits Overview */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-800 dark:text-green-200">
              Available Credits
            </span>
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-2xl font-bold text-green-900 dark:text-green-100">
            {calculatedCreditsRemaining == null ? formatCurrency(data.total_credits_available) : formatCurrency(calculatedCreditsRemaining)}
          </div>
          <div className="text-xs text-green-700 dark:text-green-300 mt-1">
            {creditCodesLabel}
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-800 dark:text-blue-200">
              Total Allocated
            </span>
            <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {formatCurrency(totalAllocated)}
          </div>
          <div className="text-xs text-blue-700 dark:text-blue-300 mt-1">
            Partner Central + Manual credits
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-800">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-orange-800 dark:text-orange-200">
              Credits Exhausted By (Est)
            </span>
            <svg className="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          {depletionInfo ? (
            <>
              <div className="text-lg font-bold text-orange-900 dark:text-orange-100">
                {depletionInfo.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </div>
              <div className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                ~{depletionInfo.daysRemaining} days remaining
              </div>
            </>
          ) : (
            <>
              <div className="text-lg font-bold text-orange-900 dark:text-orange-100">
                N/A
              </div>
              <div className="text-xs text-orange-700 dark:text-orange-300 mt-1">
                Insufficient data
              </div>
            </>
          )}
        </div>
      </div>

      {/* Funding Credit Codes List */}
      {(data.credit_codes?.length ?? 0) > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Funding Credits
          </h4>
          <div className="space-y-2">
            {data.credit_codes!.map((code) => (
              <div
                key={code.code}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-mono text-sm text-gray-900 dark:text-white truncate">
                    {code.code}
                  </div>
                  {code.aws_account_id && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      AWS Account: {code.aws_account_id}
                    </div>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 text-right">
                  <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(code.amount)}
                  </div>
                  {code.expiration_date && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Exp: {formatDate(code.expiration_date)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {data.active_credit_codes_count > data.credit_codes!.length && (
            <button
              onClick={() => navigate('/funding')}
              className="mt-3 w-full text-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium py-2"
            >
              View all {data.active_credit_codes_count} credit codes →
            </button>
          )}
        </div>
      )}

      {/* Other Credits (Manual Credits) */}
      {manualCredits.length > 0 && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Other Credits
          </h4>
          <div className="space-y-2">
            {manualCredits.slice(0, 3).map((credit) => (
              <div
                key={credit.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg border border-gray-200 dark:border-gray-700"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm text-gray-900 dark:text-white truncate font-medium">
                    {credit.credit_name}
                  </div>
                  {credit.aws_account_id && (
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      AWS Account: {credit.aws_account_id}
                    </div>
                  )}
                </div>
                <div className="ml-4 flex-shrink-0 text-right">
                  <div className="text-sm font-semibold text-green-600 dark:text-green-400">
                    {formatCurrency(credit.remaining_amount)}
                  </div>
                  {credit.end_date && (
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Exp: {formatDate(credit.end_date)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {manualCredits.length > 3 && (
            <button
              onClick={() => navigate('/funding')}
              className="mt-3 w-full text-center text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium py-2"
            >
              View all {manualCredits.length} manual credits →
            </button>
          )}
        </div>
      )}

      {/* Info Note */}
      <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
        <div className="flex items-start gap-2">
          <svg className="w-4 h-4 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="text-xs text-blue-700 dark:text-blue-300">
            <p>Funding credits are synced from AWS Partner Central and automatically matched to your AWS accounts. Other credits are manually entered. Visit the Funding page to sync latest data or add credits.</p>
            {calculatedCreditsRemaining != null && (
              <p className="mt-1.5 italic">
                Note: Available Credits shown above are calculated based on consumed credits and may differ from Partner Central due to sync timing.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerCentralCredits;
